import { getCookie, setCookie } from 'cookies-next'
import dynamic from 'next/dynamic'
import Image from 'next/image'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Text, View } from 'react-native'
import { tv } from 'tailwind-variants'
import { P, match } from 'ts-pattern'

import { Language, getLocaleParts } from '@nxt-insurance/constants'
import Button from '@nxt-insurance/ui-kit/components/Button'

import { BUTTONS_IDS, EVENTS } from '@/constants/analytics'
import { useAnalytics } from '@/context/AnalyticsContext'
import { IMAGE } from '@ui-kit/assets/images/index.native'
import ButtonIcon from '@ui-kit/components/ButtonIcon'
import Icon from '@ui-kit/components/SvgIcon'
import { body2, h3, h5 } from '@ui-kit/core/typography'

const PROMOTION_MODAL_COOKIE = 'promotion_modal_shown'

const promotionModal = tv({
  slots: {
    backdrop: 'flex flex-1 flex-col items-center justify-center bg-backdrop p-4',
    modalContainer:
      'relative mx-auto flex h-[610px] w-[90%] justify-center overflow-hidden rounded-2xl bg-ui-light pr-0 md:mt-20 md:h-auto md:w-full md:max-w-[875px]',
    closeButtonContainer: 'absolute right-5 top-4 z-10',
    modalTitle: [h5({ type: 'primary' }), 'md:', h3({ type: 'primary' }), ' pb-2 md:pb-3'],
    modalSubtitle: [body2(), 'pb-4'],
    columnsContainer: 'flex h-full flex-col-reverse md:flex-row',
    columnLeft:
      'flex h-[80%] w-full flex-1 flex-col justify-between gap-1 px-4 py-4 md:h-auto md:w-[60%] md:gap-4 md:px-12 md:pt-14',
    columnRight:
      'relative flex h-[15%] w-full flex-1 flex-col overflow-hidden bg-[#F3F3F3] md:h-auto md:w-[40%]',
    modalFooter: 'pb-4 pt-6 text-ui-grey md:pb-0 md:pt-14',
    imageContainer: 'h-full w-full object-cover',
  },
})

// Dynamically import Modal with no SSR
const Modal = dynamic(() => import('react-native').then((mod) => mod.Modal), {
  ssr: false,
})

const PromotionModal = () => {
  const {
    backdrop,
    modalContainer,
    closeButtonContainer,
    modalTitle,
    modalSubtitle,
    columnsContainer,
    columnLeft,
    columnRight,
    modalFooter,
    imageContainer,
  } = promotionModal()

  const { asPath, locale } = useRouter()
  const [lang] = getLocaleParts(locale)
  const { track } = useAnalytics()
  const [isVisible, setIsVisible] = useState(false)
  const [hasTrackedModalShown, setHasTrackedModalShown] = useState(false)
  const { t } = useTranslation()

  // Track modal shown event when modal becomes visible
  useEffect(() => {
    if (isVisible && !hasTrackedModalShown) {
      track(EVENTS.ModalShown, {
        modalType: 'promotion',
        modalName: 'travelHealthAvailable',
        t,
      })
      setHasTrackedModalShown(true)
    }
  }, [isVisible, hasTrackedModalShown, track, t])

  const maybeShowModal = useCallback(() => {
    const hasSeenModal = getCookie(PROMOTION_MODAL_COOKIE)
    if (!hasSeenModal) {
      setIsVisible(true)
      setCookie(PROMOTION_MODAL_COOKIE, 'true', { maxAge: 30 * 24 * 60 * 60 }) // 30 days
    }
  }, [])

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.addEventListener('cf_init', () => {
        const hasConsented = window.CookieFirst?.hasConsented ?? false
        if (hasConsented) {
          maybeShowModal()
        }
      })
      window.addEventListener('cf_services_consent', () => {
        maybeShowModal()
      })
      return () => {
        window.removeEventListener('cf_init', maybeShowModal)
        window.removeEventListener('cf_services_consent', maybeShowModal)
      }
    }
  }, [maybeShowModal])

  const shouldHideModal = match(asPath)
    .with(
      P.union(
        P.string.includes('liability-de'),
        P.string.includes('drone-de'),
        P.string.includes('haftpflicht-de'),
        P.string.includes('drohne-de'),
        P.string.includes('/flows/'),
        P.string.includes('-at')
      ),
      () => true
    )
    .otherwise(() => false)

  const modalPath =
    lang === Language.En ? '/assets/images/travelModalEn.png' : '/assets/images/travelModalDe.png'

  const onClose = () => {
    setIsVisible(false)
    track(EVENTS.ModalClosed, {
      modalType: 'promotion',
      modalName: 'travelHealthAvailable',
      t,
    })
  }

  const buttonHref =
    lang === Language.En
      ? 'https://product.hellogetsafe.com/travel-health'
      : ' https://product.hellogetsafe.com/de/travel-health'

  const onPress = () => {
    window.open(buttonHref, '_blank')
    track(EVENTS.ButtonClicked, {
      buttonId: BUTTONS_IDS.PROMOTION_MODAL_BUTTON,
      type: 'button',
      href: buttonHref,
      t,
    })
  }

  return (
    <Modal transparent animationType="fade" visible={isVisible && !shouldHideModal}>
      <View className={backdrop()}>
        <View className={modalContainer()}>
          <View className={columnsContainer()}>
            <View className={columnLeft()}>
              <Text className={modalTitle()}>{t('travelHealthAvailable.modal.title')}</Text>
              <Text className={modalSubtitle()}>{t('travelHealthAvailable.modal.subtitle')}</Text>
              <Button
                variantColor="brandDark"
                uppercase
                label={t('travelHealthAvailable.modal.button')}
                onPress={onPress}
              />
              <Text className={modalFooter()}>{t('travelHealthAvailable.modal.footer')}</Text>
            </View>
            <View className={columnRight()}>
              <View className={closeButtonContainer()}>
                <ButtonIcon
                  onPress={onClose}
                  icon={<Icon imgSrc={IMAGE.icons.small.action.close_24} />}
                />
              </View>
              <Image
                src={modalPath}
                width={480}
                height={100}
                loader={({ src }) => src}
                unoptimized
                alt="Travel Health Modal"
                className={imageContainer()}
              />
            </View>
          </View>
        </View>
      </View>
    </Modal>
  )
}

export default PromotionModal
